import { BigDollarInput } from "@/components/BigDollarInput/BigDollarInput";
import { DollarCell as DollarCellType } from "@/financeModels/owasco/types";
import { isNumber } from "@/utils/math";
import { formatMoney } from "@/utils/moneyFormating";
import { Box, Tooltip } from "@mantine/core";
import { PrimitiveAtom, useAtom } from "jotai";
import parseMoney from "parse-money";
import { memo, useCallback, useState } from "react";
import { MdFlipCameraAndroid } from "react-icons/md";
import { Stub } from "./cell/Stub";
import { Wrapper } from "./cell/Wrapper";
import { useTabCoordinates } from "./hooks/useTabCoordinates";

export type DollarCellProps = {
  tabX: number;
  tabY: number;
  atom: PrimitiveAtom<DollarCellType>;
};

export const DollarCell = memo(({ tabX, tabY, atom }: DollarCellProps) => {
  const [cell, setCell] = useAtom(atom);
  const [inputValue, setInputValue] = useState<number | "-" | null>(
    Number(cell.value),
  );
  const [isEditing, setIsEditing] = useState(false);

  const { isLocked, isNegative, isOverrided } = cell;

  const onPaste = useCallback(
    (value: string) => {
      const money = parseMoney(value);
      if (money == null) return;
      try {
        setCell((c) => ({ ...c, value: money.amount }));
      } catch (e) {
        console.error(e);
      }
    },
    [setCell],
  );
  const onCopy = useCallback(() => {
    return `${cell.value}`;
  }, [cell]);
  const { id, moveFocus } = useTabCoordinates({
    tabX,
    tabY,
    onPaste,
    onCopy,
  });
  const isEditMode = !isLocked && isEditing;

  const onChange = (value: number | null) => {
    setInputValue(value);
  };

  const onExitEditing = () => {
    setIsEditing(false);
    const value = isNumber(inputValue) ? inputValue : null;
    if (value === cell.value) return;
    setCell((c) => ({ ...c, value }));
  };

  const resetCell = () => {
    setInputValue(null);
    setCell((c) => ({ ...c, value: null }));
  };

  const enableEditing = (value?: null | number | "-") => {
    if (value === undefined) {
      setInputValue(Number(cell.value));
      setIsEditing(true);
      return;
    }
    if (value === null) {
      setInputValue(null);
      setIsEditing(true);
      return;
    }

    if (value === "-") {
      setInputValue("-");
      setIsEditing(true);
      return;
    }

    setInputValue(value);
    setTimeout(() => {
      setIsEditing(true);
    }, 10);
    // setIsEditing(true);
  };

  const isNegativeInverted =
    isNegative && isNumber(cell.value) && cell.value < 0;

  const displayAsNegative =
    (isNegative && !isNegativeInverted) ||
    (!isNegative && Number(cell.value) < 0);

  const cellNumberValue = Math.abs(isNumber(cell.value) ? cell.value : 0);
  const cellText = formatMoney(
    displayAsNegative ? cellNumberValue * -1 : cellNumberValue,
    undefined,
    {
      formatStyle: "accounting",
    },
  ).replace("($ 0.00)", "$ 0.00 ");

  return (
    <Wrapper id={id}>
      {!isEditMode && (
        <Stub
          animationKey={cell.value?.toString()}
          ta="right"
          resetCell={resetCell}
          moveFocus={moveFocus}
          label={
            isNumber(cell.value) && (
              <span
                style={{
                  color:
                    cellText.includes("(") &&
                    cellText.includes(")") &&
                    displayAsNegative
                      ? "var(--mantine-color-red-9)"
                      : undefined,
                  whiteSpace: "pre",
                }}
              >
                {isNegativeInverted && (
                  <Tooltip
                    ta={"center"}
                    multiline
                    w={246}
                    label="This row is negative by default. The “-” sign indicates an adjustment and flips the value to positive."
                    withArrow
                    arrowSize={12}
                  >
                    <Box display={"inline"}>
                      <MdFlipCameraAndroid
                        color={"var(--mantine-color-accent-5)"}
                        size={12}
                        style={{
                          marginRight: 4,
                        }}
                      />
                    </Box>
                  </Tooltip>
                )}
                {cellText}
              </span>
            )
          }
          enableEditing={enableEditing}
          locked={isLocked}
          overrided={isOverrided}
        />
      )}
      {isEditMode && (
        <BigDollarInput
          allowNegative={true}
          value={isNumber(inputValue) ? inputValue : undefined}
          onChange={onChange}
          autoFocus
          radius={0}
          size="sm"
          textAlign="right"
          w={"100%"}
          onBlur={onExitEditing}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === "Escape") {
              onExitEditing();
              moveFocus("down");
            }

            if (e.ctrlKey && ["z", "y"].includes(e.key)) {
              onExitEditing();
            }
          }}
        />
      )}
    </Wrapper>
  );
});
